import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { getStoryById, stories } from '../data/stories';

export default function StoryDetail() {
  const { id } = useParams<{ id: string }>();
  const story = id ? getStoryById(id) : undefined;

  if (!story) {
    return <Navigate to="/stories" replace />;
  }

  const otherStories = stories.filter(s => s.id !== id).slice(0, 3);

  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/stories" className="back-link">
              <ArrowLeft size={16} /> Back to Stories
            </Link>
            <h1>{story.name}, {story.age}</h1>
            <p>{story.journey}</p>
          </motion.div>
        </div>
      </section>

      {/* Story Teaser */}
      <section className="section">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="storydetail-teaser">
              {story.teaser}
            </p>

            <blockquote className="storydetail-quote">
              "{story.shortQuote}"
            </blockquote>

            <p className="storydetail-read-text">
              {story.name.split(' ')[0]}'s complete story—how they did it, what they faced,
              and exactly what changed—is waiting for you in the book.
            </p>

            <Link to="/join" className="btn btn-primary btn-lg">
              Get the Full Story
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Other Stories */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2>More Stories</h2>
          </div>
          <div className="grid-3">
            {otherStories.map((s) => (
              <motion.div
                key={s.id}
                className="story-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="story-card-content">
                  <h3>{s.name}, {s.age}</h3>
                  <p className="storydetail-journey-text">{s.journey}</p>
                  <p className="storydetail-short-quote">"{s.shortQuote}"</p>
                  <Link to={`/stories/${s.id}`} className="storydetail-read-more">
                    Read More →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta">
        <div className="container">
          <h2>Ready to Start Your Journey?</h2>
          <p>
            All the stories. Everything you need to begin.
          </p>
          <Link to="/join" className="btn btn-secondary btn-lg">
            Join the Community
          </Link>
        </div>
      </section>
    </>
  );
}
